// src/components/insurance/InsurancePage.jsx
import React, { useState, useEffect } from 'react';
import { Truck, Search, RefreshCw, Edit, Calendar, Trash2, X, ChevronDown, Check, AlertCircle, FileText } from 'lucide-react';
import { api } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

const Notification = ({ message, type, onClose }) => {
  if (!message) return null;
  
  return (
    <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md max-w-md z-50 flex items-center justify-between ${
      type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 
      type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' : 
      'bg-blue-100 text-blue-800 border border-blue-200'
    }`}>
      <div className="flex items-center">
        {type === 'success' && <Check className="h-5 w-5 mr-2" />}
        {type === 'error' && <AlertCircle className="h-5 w-5 mr-2" />}
        {type === 'info' && <FileText className="h-5 w-5 mr-2" />}
        <p>{message}</p>
      </div>
      <button onClick={onClose} className="ml-4 text-gray-500 hover:text-gray-700">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

const InsurancePage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [preOrderedInsurances, setPreOrderedInsurances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preOrderLoading, setPreOrderLoading] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('', 'error');
  
  // Modal states
  const [isGeorgiaModalOpen, setIsGeorgiaModalOpen] = useState(false);
  const [isRussiaModalOpen, setIsRussiaModalOpen] = useState(false);
  const [isInsuranceDetailsModalOpen, setIsInsuranceDetailsModalOpen] = useState(false);
  const [selectedVehicleForInsurance, setSelectedVehicleForInsurance] = useState(null);
  const [selectedInsuranceCountry, setSelectedInsuranceCountry] = useState('ge', 'error');
  const [isViewMode, setIsViewMode] = useState(false);
  const [documentsToDelete, setDocumentsToDelete] = useState([]);
  
  // Dropdown states - ADD THESE
  const [showGeorgiaDropdown, setShowGeorgiaDropdown] = useState(false);
  const [showRussiaDropdown, setShowRussiaDropdown] = useState(false);
  const [georgiaSearchText, setGeorgiaSearchText] = useState('', 'error');
  const [russiaSearchText, setRussiaSearchText] = useState('', 'error');
  const [isPreOrderedModalOpen, setIsPreOrderedModalOpen] = useState(false);
  const [preOrderSearchQuery, setPreOrderSearchQuery] = useState('', 'error');
  const [editingPreOrderId, setEditingPreOrderId] = useState(null);
  const [preOrderEditMode, setPreOrderEditMode] = useState(''); // 'georgia', 'russia', 'armenia'
  const [notification, setNotification] = useState({ message: '', type: '' });
  const { language, setLanguage, t } = useLanguage();
  
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 5000);
  };
  
  const [georgiaFormData, setGeorgiaFormData] = useState({
    plateNumber: '',
    duration: '',
    startDate: ''
  });

  const [russiaFormData, setRussiaFormData] = useState({
    plateNumber: '',
    startDate: '',
    attachedFiles: []
  });
  

  const [insuranceDetailsData, setInsuranceDetailsData] = useState({
    plateNumber: '',
    dateFrom: '',
    dateTo: '',
    policyNumber: '',
    vin: '',
    driverName: '',
    driverPassport: '',
    attachedFiles: [],
    existingDocuments: []
  });

  useEffect(() => {
    fetchVehicles();
    fetchInsurances();
    fetchPreOrderedInsurances();
  }, []);

  const fetchVehicles = async () => {
    try {
      const data = await api.getVehicles();
      setVehicles(data);
      return data;
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      showNotification('Failed to load vehicles. Please try again.', 'error');
      return [];
    }
  };

  const fetchInsurances = async () => {
    try {
      setLoading(true);
      const data = await api.getInsurances();
      setInsurances(data);
    } catch (error) {
      console.error('Error fetching insurances:', error);
      showNotification('Failed to load insurances. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  
  const handleDeleteDocument = (docId) => {
  setDocumentsToDelete(prev => [...prev, docId]);
  // Remove from existingDocuments display
  setInsuranceDetailsData(prev => ({
    ...prev,
    existingDocuments: prev.existingDocuments.filter(doc => doc.id !== docId)
  }));
};

const handleRestoreDocument = (docId, doc) => {
  setDocumentsToDelete(prev => prev.filter(id => id !== docId));
  // Add back to existingDocuments
  setInsuranceDetailsData(prev => ({
    ...prev,
    existingDocuments: [...prev.existingDocuments, doc]
  }));
};

const fetchPreOrderedInsurances = async () => {
  try {
    setPreOrderLoading(true);
    const data = await api.getPreOrderedInsurances();
    setPreOrderedInsurances(data);
  } catch (error) {
    console.error('Error fetching pre-ordered insurances:', error);
    showNotification('Failed to load pre-ordered insurances. Please try again.', 'error');
  } finally {
    setPreOrderLoading(false);
  }
};

  const handleRefresh = () => {
    fetchVehicles();
    fetchInsurances();
    fetchPreOrderedInsurances();
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  // Получить страховки для конкретного транспортного средства
  const getVehicleInsurances = (vehicleId) => {
    return insurances.filter(insurance => insurance.vehicle === vehicleId);
  };

  // Получить страховку для конкретной страны
  const getVehicleInsuranceByCountry = (vehicleId, country) => {
    return insurances.find(insurance => 
      insurance.vehicle === vehicleId && insurance.country === country
    );
  };
  
  const getInsuranceDateColor = (dateToString) => {
  if (!dateToString) return 'text-gray-500';
  
  const dateTo = new Date(dateToString);
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0); // Reset time to start of day
  dateTo.setHours(0, 0, 0, 0); // Reset time to start of day
  
  const daysRemaining = Math.floor((dateTo - currentDate) / (1000 * 60 * 60 * 24));
  
  if (daysRemaining <= 0) {
    return 'text-red-600 font-semibold'; // Expired or expires today
  } else if (daysRemaining <= 5) {
    return 'text-orange-400 font-semibold'; // Expires within 5 days
  } else {
    return 'text-green-600 font-semibold'; // Good for more than 5 days
  }
};

  // Функция для обеспечения существования транспортного средства
// Function to ensure vehicle exists
  const ensureVehicleExists = async (plateNumber, vehicleData = {}) => {
    try {
      
      // Step 1: Query API for this specific plate number
      const freshVehicles = await api.getVehicles(plateNumber);
      
      let vehicle = freshVehicles.find(v => 
        v.plate_number.toLowerCase() === plateNumber.toLowerCase()
      );
      
      if (vehicle) {
        
        // Check if we need to update vehicle data
        const needsUpdate = (
          (vehicleData.vin && vehicleData.vin !== vehicle.vin) ||
          (vehicleData.driverName && vehicleData.driverName !== vehicle.driver_name) ||
          (vehicleData.driverPassport && vehicleData.driverPassport !== vehicle.driver_passport_number)
        );

        if (needsUpdate) {
          const updateData = {};
          if (vehicleData.vin && vehicleData.vin !== vehicle.vin) {
            updateData.vin = vehicleData.vin;
          }
          if (vehicleData.driverName && vehicleData.driverName !== vehicle.driver_name) {
            updateData.driver_name = vehicleData.driverName;
          }
          if (vehicleData.driverPassport && vehicleData.driverPassport !== vehicle.driver_passport_number) {
            updateData.driver_passport_number = vehicleData.driverPassport;
          }
          
          vehicle = await api.updateVehicle(vehicle.id, updateData);
        }
        

        return vehicle;
      }
      
      // Step 2: Vehicle doesn't exist, create new one

      
      const newVehicleData = {
        plate_number: plateNumber,
        vin: vehicleData.vin || '',
        driver_name: vehicleData.driverName || '',
        driver_passport_number: vehicleData.driverPassport || ''
      };
      

      
      vehicle = await api.addVehicle(newVehicleData);

      
      if (!vehicle || !vehicle.id) {
        throw new Error('Vehicle creation failed - no ID returned', 'error');
      }
      
      // Update local state to include new vehicle
      setVehicles(prev => {
        const updated = [...prev, vehicle];

        return updated;
      });
      

      return vehicle;
      
    } catch (error) {
      console.error('Error ensuring vehicle exists:', error);
      throw new Error(`Failed to ensure vehicle exists: ${error.message}`, 'success');
    }
  };

const handleEditInsurance = (vehicleId) => {
  const vehicle = vehicles.find(v => v.id === vehicleId);
  if (vehicle) {
    setSelectedVehicleForInsurance(vehicle);
    setSelectedInsuranceCountry('ge', 'error');
    setIsViewMode(false);
    
    const insurance = getVehicleInsuranceByCountry(vehicleId, 'ge', 'error');
    
    // Fallback logic: if Insurance field is empty, use Vehicle data
    const vin = insurance?.vin ? insurance.vin : (vehicle.vin || '');
    const driverName = insurance?.driver_name ? insurance.driver_name : (vehicle.driver_name || '');
    const driverPassport = insurance?.driver_passport_number ? insurance.driver_passport_number : (vehicle.driver_passport_number || '');
    
    setInsuranceDetailsData({
      plateNumber: vehicle.plate_number,
      dateFrom: insurance?.date_from || '',
      dateTo: insurance?.date_to || '',
      policyNumber: insurance?.policy_number || '',
      vin: vin,
      driverName: driverName,
      driverPassport: driverPassport,
      attachedFiles: [],
      existingDocuments: insurance?.documents || []
    });
    
    setIsInsuranceDetailsModalOpen(true);
  }
};

const handleViewInsurance = (vehicleId) => {
  const vehicle = vehicles.find(v => v.id === vehicleId);
  if (vehicle) {
    setSelectedVehicleForInsurance(vehicle);
    setSelectedInsuranceCountry('ge', 'error');
    setIsViewMode(true);
    
    const insurance = getVehicleInsuranceByCountry(vehicleId, 'ge', 'error');
    
    // Fallback logic: if Insurance field is empty, use Vehicle data
    const vin = insurance?.vin ? insurance.vin : (vehicle.vin || '');
    const driverName = insurance?.driver_name ? insurance.driver_name : (vehicle.driver_name || '');
    const driverPassport = insurance?.driver_passport_number ? insurance.driver_passport_number : (vehicle.driver_passport_number || '');
    
    setInsuranceDetailsData({
      plateNumber: vehicle.plate_number,
      dateFrom: insurance?.date_from || '',
      dateTo: insurance?.date_to || '',
      policyNumber: insurance?.policy_number || '',
      vin: vin,
      driverName: driverName,
      driverPassport: driverPassport,
      attachedFiles: [],
      existingDocuments: insurance?.documents || []
    });
    
    setIsInsuranceDetailsModalOpen(true);
  }
};

const handleEditPreOrder = (preOrderId) => {
  const preOrder = preOrderedInsurances.find(order => order.id === preOrderId);
  if (preOrder) {
    setEditingPreOrderId(preOrderId);
    
    if (preOrder.insurance_ge && preOrder.insurance_ge !== '-') {
      setGeorgiaFormData({
        plateNumber: preOrder.plate_number,
        duration: preOrder.insurance_ge,
        startDate: preOrder.start_date
      });
      setPreOrderEditMode('georgia', 'error');
      setIsGeorgiaModalOpen(true);
    } else if (preOrder.insurance_ru && preOrder.insurance_ru !== '-') {
      setRussiaFormData({
        plateNumber: preOrder.plate_number,
        startDate: preOrder.start_date
      });
      setPreOrderEditMode('russia', 'error');
      setIsRussiaModalOpen(true);
    } else if (preOrder.insurance_am && preOrder.insurance_am !== '-') {
      setGeorgiaFormData({
        plateNumber: preOrder.plate_number,
        duration: preOrder.insurance_am,
        startDate: preOrder.start_date
      });
      setPreOrderEditMode('armenia', 'error');
      setIsGeorgiaModalOpen(true);
    }
  }
};

// 5. Update handleDeletePreOrder to use the API:
const handleDeletePreOrder = async (preOrderId) => {
  if (window.confirm(t('confirmDelete'))) {
    try {
      await api.deletePreOrderedInsurance(preOrderId);
      await fetchPreOrderedInsurances();
      showNotification(t('registrationDeleted'), 'success');
    } catch (error) {
      showNotification('Failed to delete: ' + error.message, 'error');
    }
  }
}

  const handleCountryInsuranceAction = (country) => {
    if (country === 'ge') {
      setIsGeorgiaModalOpen(true);
    } else if (country === 'ru') {
      setIsRussiaModalOpen(true);
    } else {

      showNotification(`Bulk action for ${country} insurance - this could open a bulk edit modal`, 'info');
    }
  };

const handleExportInsurancesToExcel = () => {
  if (filteredVehicles.length === 0) {
    showNotification('No data to export', 'info');
    return;
  }

  // Create HTML table for Excel
  let tableHTML = '<table border="1">';
  
  // Add main headers with colspan
  tableHTML += '<thead><tr>';
  tableHTML += '<th rowspan="2">Plate Number</th>';
  tableHTML += '<th colspan="2">Insurance GE</th>';
  tableHTML += '<th colspan="2">Insurance RU</th>';
  tableHTML += '<th colspan="2">Insurance AM</th>';
  tableHTML += '</tr>';
  
  // Add sub headers
  tableHTML += '<tr>';
  tableHTML += '<th>Date from</th>';
  tableHTML += '<th>Date to</th>';
  tableHTML += '<th>Date from</th>';
  tableHTML += '<th>Date to</th>';
  tableHTML += '<th>Date from</th>';
  tableHTML += '<th>Date to</th>';
  tableHTML += '</tr></thead>';
  
  // Add data rows
  tableHTML += '<tbody>';
  filteredVehicles.forEach((vehicle) => {
    tableHTML += '<tr>';
    tableHTML += `<td>${vehicle.plate_number}</td>`;
    
    const geInsurance = getVehicleInsuranceByCountry(vehicle.id, 'ge', 'error');
    tableHTML += `<td>${geInsurance?.date_from || '-'}</td>`;
    tableHTML += `<td>${geInsurance?.date_to || '-'}</td>`;
    
    const ruInsurance = getVehicleInsuranceByCountry(vehicle.id, 'ru', 'error');
    tableHTML += `<td>${ruInsurance?.date_from || '-'}</td>`;
    tableHTML += `<td>${ruInsurance?.date_to || '-'}</td>`;
    
    const amInsurance = getVehicleInsuranceByCountry(vehicle.id, 'am', 'error');
    tableHTML += `<td>${amInsurance?.date_from || '-'}</td>`;
    tableHTML += `<td>${amInsurance?.date_to || '-'}</td>`;
    
    tableHTML += '</tr>';
  });
  tableHTML += '</tbody></table>';

  // Create blob with proper Excel format
  const blob = new Blob([tableHTML], { 
    type: 'application/vnd.ms-excel' 
  });
  
  const link = document.createElement('a', 'error');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `insurance_records_${new Date().toISOString().split('T')[0]}.xls`, 'success');
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  setTimeout(() => URL.revokeObjectURL(url), 100);
};
  
const handleExportPreOrderedToExcel = () => {
  if (filteredPreOrderedInsurances.length === 0) {
    showNotification('No data to export', 'info');
    return;
  }

  // Create HTML table for Excel
  let tableHTML = '<table border="1">';
  
  // Add headers
  tableHTML += '<thead><tr>';
  const headers = [
    'N', 'Plate Number', 'Insurance GE', 'Insurance RU', 'Insurance AM', 
    'Start Date', 'Ordered Date'
  ];
  headers.forEach(header => {
    tableHTML += `<th>${header}</th>`;
  });
  tableHTML += '</tr></thead>';
  
  // Add data rows
  tableHTML += '<tbody>';
  filteredPreOrderedInsurances.forEach((preOrder, index) => {
    tableHTML += '<tr>';
    tableHTML += `<td>${index + 1}</td>`;
    tableHTML += `<td>${preOrder.plate_number}</td>`;
    tableHTML += `<td>${preOrder.insurance_ge || '-'}</td>`;
    tableHTML += `<td>${preOrder.insurance_ru || '-'}</td>`;
    tableHTML += `<td>${preOrder.insurance_am || '-'}</td>`;
    tableHTML += `<td>${preOrder.start_date}</td>`;
    tableHTML += `<td>${preOrder.ordered_date}</td>`;
    tableHTML += '</tr>';
  });
  tableHTML += '</tbody></table>';

  // Create blob with proper Excel format
  const blob = new Blob([tableHTML], { 
    type: 'application/vnd.ms-excel' 
  });
  
  const link = document.createElement('a', 'error');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `pre-ordered-insurances_${new Date().toISOString().split('T')[0]}.xls`, 'success');
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  setTimeout(() => URL.revokeObjectURL(url), 100);
};
  const handleGeorgiaModalClose = () => {
    setIsGeorgiaModalOpen(false);
    setGeorgiaFormData({
      plateNumber: '',
      duration: '',
      startDate: ''
    });
    setEditingPreOrderId(null);
    setPreOrderEditMode('', 'error');
  };

const handleGeorgiaFormSubmit = async (e) => {
  e.preventDefault();
  try {

    
    if (editingPreOrderId) {
      await api.updatePreOrderedInsurance(editingPreOrderId, {
        insurance_ge: georgiaFormData.duration,
        start_date: georgiaFormData.startDate
      });
      showNotification(t('invoiceUpdated'), 'success');
    } else {
      const preOrderData = {
        plate_number: georgiaFormData.plateNumber,
        insurance_ge: georgiaFormData.duration,
        start_date: georgiaFormData.startDate
      };
      
      await api.createPreOrderedInsurance(preOrderData);
      showNotification(`Georgia pre-ordered insurance created for plate ${georgiaFormData.plateNumber}`, 'success');
    }
    
    handleGeorgiaModalClose();
    
    // Refresh pre-ordered insurances
    await fetchPreOrderedInsurances();
    
  } catch (error) {
    console.error('Error creating Georgia pre-ordered insurance:', error);
    showNotification(`Failed to create pre-ordered insurance: ${error.message}`, 'error');
  }
};


  // Вспомогательная функция для расчета даты окончания
  const calculateEndDate = (startDate, duration) => {
    const start = new Date(startDate);
    let end = new Date(start);
    
    switch (duration) {
      case 'days_15':
        end.setDate(start.getDate() + 15);
        break;
      case 'days_30':
        end.setDate(start.getDate() + 30);
        break;
      case 'days_90':
        end.setDate(start.getDate() + 90);
        break;
      case 'year_1':
        end.setFullYear(start.getFullYear() + 1);
        break;
      default:
        end.setDate(start.getDate() + 30);
    }
    
    return end.toISOString().split('T')[0];
  };

  const handleGeorgiaFormChange = (field, value) => {
    setGeorgiaFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRussiaModalClose = () => {
    setIsRussiaModalOpen(false);
    setRussiaFormData({
      plateNumber: '',
      startDate: '',
      attachedFiles: []
    });
    setEditingPreOrderId(null);
    setPreOrderEditMode('', 'error');
  };

const handleRussiaFormSubmit = async (e) => {
  e.preventDefault();
  try {

    
    if (editingPreOrderId) {
      const formDataToSend = new FormData();
      formDataToSend.append('insurance_ru', 'year_');
      formDataToSend.append('start_date', russiaFormData.startDate);
      
      if (russiaFormData.attachedFiles && russiaFormData.attachedFiles.length > 0) {
        russiaFormData.attachedFiles.forEach((file) => {
          if (file instanceof File) {
            formDataToSend.append('documents', file);
          }
        });
      }
      
      await api.updatePreOrderedInsurance(editingPreOrderId, formDataToSend);
      showNotification(t('invoiceUpdated'), 'success');
    } else {
      const formDataToSend = new FormData();
      formDataToSend.append('plate_number', russiaFormData.plateNumber);
      formDataToSend.append('insurance_ru', 'year_1');
      formDataToSend.append('start_date', russiaFormData.startDate);
      
      if (russiaFormData.attachedFiles && russiaFormData.attachedFiles.length > 0) {
        russiaFormData.attachedFiles.forEach((file) => {
          if (file instanceof File) {
            formDataToSend.append('documents', file);
          }
        });
      }
      
      await api.createPreOrderedInsurance(formDataToSend);
      showNotification(`Russia pre-ordered insurance created for plate ${russiaFormData.plateNumber}`, 'success');
    }
    
    handleRussiaModalClose();
    
    // Refresh pre-ordered insurances
    await fetchPreOrderedInsurances();
    
  } catch (error) {
    console.error('Error creating Russia pre-ordered insurance:', error);
    showNotification(`Failed to create pre-ordered insurance: ${error.message}`, 'error');
  }
};

  const handleRussiaFormChange = (field, value) => {
    if (field === 'attachedFiles') {
      setRussiaFormData(prev => ({
        ...prev,
        [field]: [...(prev.attachedFiles || []), ...value]
      }));
    } else {
      setRussiaFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleInsuranceDetailsModalClose = () => {
    setIsInsuranceDetailsModalOpen(false);
    setSelectedVehicleForInsurance(null);
    setSelectedInsuranceCountry('ge', 'error');
    setIsViewMode(false);
    setInsuranceDetailsData({
      plateNumber: '',
      dateFrom: '',
      dateTo: '',
      policyNumber: '',
      vin: '',
      driverName: '',
      driverPassport: '',
      attachedFiles: [],
      existingDocuments: []
    });
  };

const handleInsuranceCountryChange = (country) => {
  setSelectedInsuranceCountry(country);
  
  const vehicle = selectedVehicleForInsurance;
  if (vehicle) {
    const insurance = getVehicleInsuranceByCountry(vehicle.id, country);
    
    // Fallback logic: if Insurance field is empty, use Vehicle data
    const vin = insurance?.vin ? insurance.vin : (vehicle.vin || '', 'error');
    const driverName = insurance?.driver_name ? insurance.driver_name : (vehicle.driver_name || '', 'error');
    const driverPassport = insurance?.driver_passport_number ? insurance.driver_passport_number : (vehicle.driver_passport_number || '', 'error');
    
    setInsuranceDetailsData({
      plateNumber: vehicle.plate_number,
      dateFrom: insurance?.date_from || '',
      dateTo: insurance?.date_to || '',
      policyNumber: insurance?.policy_number || '',
      vin: vin,
      driverName: driverName,
      driverPassport: driverPassport,
      attachedFiles: [],
      existingDocuments: insurance?.documents || []
    });
  }
};

  const handleInsuranceDetailsChange = (field, value) => {
    setInsuranceDetailsData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInsuranceFileChange = (event) => {
    const files = Array.from(event.target.files);
    setInsuranceDetailsData(prev => ({
      ...prev,
      attachedFiles: [...prev.attachedFiles, ...files]
    }));
  };

  const handleRemoveFile = (index) => {
    setInsuranceDetailsData(prev => ({
      ...prev,
      attachedFiles: prev.attachedFiles.filter((_, i) => i !== index)
    }));
  };

const handleInsuranceDetailsSubmit = async (e) => {
  e.preventDefault();
  
  // Validate required fields
  if (!insuranceDetailsData.dateFrom || !insuranceDetailsData.dateTo) {
    showNotification('Please fill in both Date From and Date To fields', 'error');
    return;
  }

  if (!insuranceDetailsData.plateNumber) {
    showNotification('No plate number provided', 'error');
    return;
  }

  try {

    
    // Step 1: Get FRESH vehicle data from API by plate number

    const vehiclesData = await api.getVehicles(insuranceDetailsData.plateNumber);

    
    if (!vehiclesData || vehiclesData.length === 0) {
      throw new Error('Vehicle not found. Please register the vehicle first.', 'error');
    }
    
    // Find the exact vehicle by plate number
    const freshVehicle = vehiclesData.find(v => 
      v.plate_number.toLowerCase() === insuranceDetailsData.plateNumber.toLowerCase()
    );
    
    if (!freshVehicle) {
      throw new Error(`Vehicle with plate ${insuranceDetailsData.plateNumber} not found`, 'success');
    }
    


    // Step 2: Prepare insurance data WITH vehicle details
    const insuranceData = {
      vehicle: freshVehicle.id,
      country: selectedInsuranceCountry,
      date_from: insuranceDetailsData.dateFrom,
      date_to: insuranceDetailsData.dateTo,
      policy_number: insuranceDetailsData.policyNumber || '',
      vin: insuranceDetailsData.vin || '',
      driver_name: insuranceDetailsData.driverName || '',
      driver_passport_number: insuranceDetailsData.driverPassport || ''
    };



    // Step 3: Create or update insurance
    let insuranceResponse;
    
    // Fetch latest insurances to check if one exists
    const latestInsurances = await api.getInsurances(freshVehicle.id, selectedInsuranceCountry);

    
    const existingInsurance = latestInsurances.find(ins => 
      ins.vehicle === freshVehicle.id && ins.country === selectedInsuranceCountry
    );

    if (existingInsurance) {

      insuranceResponse = await api.updateInsuranceRecord(existingInsurance.id, insuranceData);
    } else {

      insuranceResponse = await api.createInsurance(insuranceData);
    }



    // Step 4: Delete documents marked for deletion
    if (documentsToDelete.length > 0 && insuranceResponse.id) {

      for (const docId of documentsToDelete) {
        try {
          await api.deleteInsuranceDocument(docId);

        } catch (docError) {
          console.warn('Failed to delete document:', docId, docError);
        }
      }
      setDocumentsToDelete([]);
    }

    // Step 5: Upload new files if any
    if (insuranceDetailsData.attachedFiles.length > 0 && insuranceResponse.id) {

      for (const file of insuranceDetailsData.attachedFiles) {
        try {
          await api.uploadInsuranceDocument(insuranceResponse.id, file);

        } catch (uploadError) {
          console.warn('Failed to upload file:', file.name, uploadError);
        }
      }
    }

    showNotification(t('registrationUpdated'), 'success');
    
    // Refresh insurances
    await fetchInsurances();
    
    // Close modal
    handleInsuranceDetailsModalClose();
    
  } catch (error) {
    console.error('Error during insurance submission:', error);
    showNotification('Failed to update insurance: ' + error.message, 'error');
  }
};


  // Получить даты страховки для отображения в таблице
  const getInsuranceDates = (vehicleId, country) => {
    const insurance = getVehicleInsuranceByCountry(vehicleId, country);
    
    return {
      dateFrom: insurance?.date_from || '-',
      dateTo: insurance?.date_to || '-',
      exists: !!insurance
    };
  };

  const hasAnyInsurance = (vehicleId) => {
    return getInsuranceDates(vehicleId, 'ge').exists || 
           getInsuranceDates(vehicleId, 'ru').exists || 
           getInsuranceDates(vehicleId, 'am').exists;
  };

  const filteredVehicles = vehicles.filter(vehicle =>
    vehicle.plate_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (vehicle.vin && vehicle.vin.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  const totalPages = Math.ceil(filteredVehicles.length / pageSize);
  const pagedVehicles = filteredVehicles.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const filteredPreOrderedInsurances = preOrderedInsurances.filter(preOrder =>
  preOrder.plate_number.toLowerCase().includes(preOrderSearchQuery.toLowerCase())
);

  return (
    <div className="container mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6">
      {/* Insurance Action Buttons */}
      <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
        <button 
          onClick={() => handleCountryInsuranceAction('ge')}
          className="px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center text-xs sm:text-sm"
        >
		{t('getInsuranceGE')}
        </button>
        <button 
          onClick={() => handleCountryInsuranceAction('ru')}
          className="px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center text-xs sm:text-sm"
        >
		{t('getInsuranceRU')}
        </button>
        <button 
          onClick={handleExportInsurancesToExcel}
          className="px-2 sm:px-3 py-1.5 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center text-xs sm:text-sm"
        >
         {t('exportExcel')}
        </button>
		
      </div>
<div className="mb-6">
  <button
    onClick={() => setIsPreOrderedModalOpen(true)}
    className="text-blue-600 hover:text-blue-800 underline hover:underline text-sm font-medium cursor-pointer bg-none border-none p-0"
  >
    {t('viewOrderedInsurances')}
  </button>
</div>

{/* Georgia Insurance Modal */}
      {isGeorgiaModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">{t('getInsuranceGE')}</h3>
              <button 
                onClick={handleGeorgiaModalClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleGeorgiaFormSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="plateNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('licensePlate')}
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowGeorgiaDropdown(!showGeorgiaDropdown)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left flex justify-between items-center bg-white hover:bg-gray-50"
					required
                  >
                    <span>{georgiaFormData.plateNumber || t('selectFrmVehFleet')}</span>
                    <ChevronDown className={`h-5 w-5 transition-transform ${showGeorgiaDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showGeorgiaDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      <div className="p-2 border-b border-gray-200">
                        <input
                          type="text"
                          placeholder={t('search')}
                          value={georgiaSearchText}
                          onChange={(e) => setGeorgiaSearchText(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {vehicles
                          .filter(v => v.plate_number.toLowerCase().includes(georgiaSearchText.toLowerCase()))
                          .map(vehicle => (
                            <button
                              key={vehicle.id}
                              type="button"
                              onClick={() => {
                                handleGeorgiaFormChange('plateNumber', vehicle.plate_number);
                                setShowGeorgiaDropdown(false);
                                setGeorgiaSearchText('', 'error');
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                            >
                              <div className="font-medium text-gray-900">{vehicle.plate_number}</div>
                              <div className="text-xs text-gray-500">{t(vehicle.type) || 'Vehicle'}</div>
                            </button>
                          ))
                          .slice(0, 10)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('duration')}
                </label>
                <select
                  id="duration"
                  value={georgiaFormData.duration}
                  onChange={(e) => handleGeorgiaFormChange('duration', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">{t('select')}</option>
                  <option value="days_15">{t('days_15')}</option>
                  <option value="days_30">{t('days_30')}</option>
                  <option value="days_90">{t('days_90')}</option>
                  <option value="year_1">{t('year_1')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                   {t('startDate')}
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={georgiaFormData.startDate}
                  onChange={(e) => handleGeorgiaFormChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleGeorgiaModalClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                   {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  {t('getInsurance')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Russia Insurance Modal */}
      {isRussiaModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900"> {t('getInsuranceRU')}</h3>
              <button 
                onClick={handleRussiaModalClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleRussiaFormSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="russiaPlateNumber" className="block text-sm font-medium text-gray-700 mb-1">
                   {t('licensePlate')}
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowRussiaDropdown(!showRussiaDropdown)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left flex justify-between items-center bg-white hover:bg-gray-50"
                  >
                    <span>{russiaFormData.plateNumber ||  t('selectFrmVehFleet')}</span>
                    <ChevronDown className={`h-5 w-5 transition-transform ${showRussiaDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showRussiaDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      <div className="p-2 border-b border-gray-200">
                        <input
                          type="text"
                          placeholder= {t('search')}
                          value={russiaSearchText}
                          onChange={(e) => setRussiaSearchText(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {vehicles
                          .filter(v => 
                            v.plate_number.toLowerCase().includes(russiaSearchText.toLowerCase()) &&
                            v.type !== 'semi_trailer'
                          )
                          .map(vehicle => (
                            <button
                              key={vehicle.id}
                              type="button"
                              onClick={() => {
                                handleRussiaFormChange('plateNumber', vehicle.plate_number);
                                setShowRussiaDropdown(false);
                                setRussiaSearchText('', 'error');
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                            >
                              <div className="font-medium text-gray-900">{vehicle.plate_number}</div>
                              <div className="text-xs text-gray-500">{vehicle.type || 'Vehicle'}</div>
                            </button>
                          ))
                          .slice(0, 10)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="russiaDuration" className="block text-sm font-medium text-gray-700 mb-1">
                   {t('duration')}
                </label>
                <input
                  type="text"
                  id="russiaDuration"
                  value={t('year')}
                  readOnly
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-gray-100 text-gray-600 cursor-not-allowed"
                />

              </div>

              <div>
                <label htmlFor="russiaStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('startDate')}
                </label>
                <input
                  type="date"
                  id="russiaStartDate"
                  value={russiaFormData.startDate}
                  onChange={(e) => handleRussiaFormChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="russiaFiles" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('attachFile')}
                </label>
                <input
                  type="file"
                  id="russiaFiles"
                  multiple
                  onChange={(e) => handleRussiaFormChange('attachedFiles', Array.from(e.target.files))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {russiaFormData.attachedFiles && russiaFormData.attachedFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {russiaFormData.attachedFiles.map((file, idx) => (
                      <p key={idx} className="text-sm text-gray-600">{file.name || file}</p>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleRussiaModalClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  {t('getInsurance')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
{/* Pre-ordered Insurances Modal */}
{isPreOrderedModalOpen && (
  <div className="fixed inset-0 z-40 overflow-y-auto">
    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      {/* Background overlay */}
      <div 
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={() => setIsPreOrderedModalOpen(false)}
      ></div>

      {/* Modal panel */}
      <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          {/* Modal Header */}
          <div className="flex justify-between items-center mb-4 border-b pb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
             {t('orderedInsurances')}
            </h3>
            <button
              onClick={() => setIsPreOrderedModalOpen(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Search Field */}
          <div className="mb-4">
            <div className="relative max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={preOrderSearchQuery}
                onChange={(e) => setPreOrderSearchQuery(e.target.value)}
                placeholder={t('search')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
		  			  <button
          onClick={handleExportPreOrderedToExcel}
          className="px-2 sm:px-3 py-1.5 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center text-xs sm:text-sm"
        >
          {t('exportExcel')}
  </button>

          {/* Modal Content */}
          {preOrderLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading pre-ordered insurances...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                      {t('licensePlate')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                      {t('insuranceGE')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                      {t('insuranceRU')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                      {t('insuranceAM')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                      {t('startDate')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                      {t('orderDate')}
                    </th>
					<th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                      {t('signed')}
                    </th>
					<th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                      {t('paid')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                      {t('actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPreOrderedInsurances.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        <div className="flex flex-col items-center justify-center">
                          <Calendar className="h-12 w-12 text-gray-400 mb-2" />
                          <p className="text-lg font-medium text-gray-900 mb-1">
                            {preOrderSearchQuery ? 'No pre-ordered insurances found' : 'No pre-ordered insurances'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {preOrderSearchQuery ? 'Try a different search term' : 'Pre-ordered insurances will appear here'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
filteredPreOrderedInsurances.map((preOrder) => (
                      <tr key={preOrder.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                          {preOrder.plate_number}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200">
                          {preOrder.insurance_ge ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {t(preOrder.insurance_ge)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200">
                          {preOrder.insurance_ru ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {t(preOrder.insurance_ru)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200">
                          {preOrder.insurance_am ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {t(preOrder.insurance_am)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                          {preOrder.start_date}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                          {preOrder.ordered_date}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            preOrder.is_signed 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {preOrder.is_signed === true || preOrder.is_signed === 'true' || preOrder.is_signed === 1 ? t('yes') : t('no')}
                          </span>
                        </td>
						<td className="px-4 py-3 whitespace-nowrap text-sm text-center border-r border-gray-200">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            preOrder.is_paid 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {preOrder.is_paid === true || preOrder.is_paid === 'true' || preOrder.is_paid === 1 ? t('yes') : t('no')}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                          <div className="flex justify-center space-x-2">
                            <button 
                              onClick={() => handleEditPreOrder(preOrder.id)}
                              disabled={preOrder.is_signed}
                              className={`${
                                preOrder.is_signed
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-blue-600 hover:text-blue-900'
                              }`}
                              title={(preOrder.is_signed === true || preOrder.is_signed === 'true' || preOrder.is_signed === 1) ? t('cannotEdit') : t('edit')}
                              >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleDeletePreOrder(preOrder.id)}
                              disabled={preOrder.is_signed}
                              className={`${
                                preOrder.is_signed
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-red-600 hover:text-red-900'
                              }`}
                              title={(preOrder.is_signed === true || preOrder.is_signed === 'true' || preOrder.is_signed === 1) ? t('cannotDelete') : t('delete')}
                               >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
          <button
            onClick={() => setIsPreOrderedModalOpen(false)}
            className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  </div>
)}
      {/* Insurance Details Modal */}
      {isInsuranceDetailsModalOpen && selectedVehicleForInsurance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-8">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedVehicleForInsurance.plate_number}
              </h3>
              <button 
                onClick={handleInsuranceDetailsModalClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleInsuranceDetailsSubmit} className="p-6">
              {/* Insurance Country Selection */}
              <div className="mb-6">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleInsuranceCountryChange('ge')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                      selectedInsuranceCountry === 'ge'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="inline-block w-6 h-4 bg-gradient-to-r from-red-600 to-white rounded"></span>
                      <span className="font-medium">{t('insuranceGE')}</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInsuranceCountryChange('ru')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                      selectedInsuranceCountry === 'ru'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="inline-block w-6 h-4 bg-gradient-to-b from-white via-blue-500 to-red-600 rounded"></span>
                      <span className="font-medium">{t('insuranceRU')}</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInsuranceCountryChange('am')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                      selectedInsuranceCountry === 'am'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="inline-block w-6 h-4 bg-gradient-to-b from-red-600 via-blue-500 to-orange-500 rounded"></span>
                      <span className="font-medium">{t('insuranceAM')}</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('licensePlate')}
                    </label>
                    <input
                      type="text"
                      value={insuranceDetailsData.plateNumber}
                      onChange={(e) => handleInsuranceDetailsChange('plateNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                      disabled={isViewMode}
                      readOnly={isViewMode}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      VIN
                    </label>
                    <input
                      type="text"
                      value={insuranceDetailsData.vin}
                      onChange={(e) => handleInsuranceDetailsChange('vin', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      disabled={isViewMode}
                      readOnly={isViewMode}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('start')}
                    </label>
                    <input
                      type="date"
                      value={insuranceDetailsData.dateFrom}
                      onChange={(e) => handleInsuranceDetailsChange('dateFrom', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                      disabled={isViewMode}
                      readOnly={isViewMode}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('end')}
                    </label>
                    <input
                      type="date"
                      value={insuranceDetailsData.dateTo}
                      onChange={(e) => handleInsuranceDetailsChange('dateTo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                      disabled={isViewMode}
                      readOnly={isViewMode}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('policyNumber')}
                  </label>
                  <input
                    type="text"
                    value={insuranceDetailsData.policyNumber}
                    onChange={(e) => handleInsuranceDetailsChange('policyNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder=""
                    disabled={isViewMode}
                    readOnly={isViewMode}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('driverInfo')}
                    </label>
                    <input
                      type="text"
                      value={insuranceDetailsData.driverName}
                      onChange={(e) => handleInsuranceDetailsChange('driverName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      disabled={isViewMode}
                      readOnly={isViewMode}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('driverPass')}
                    </label>
                    <input
                      type="text"
                      value={insuranceDetailsData.driverPassport}
                      onChange={(e) => handleInsuranceDetailsChange('driverPassport', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      disabled={isViewMode}
                      readOnly={isViewMode}
                    />
                  </div>
                </div>



{!isViewMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('attachFile')}
                    </label>
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleInsuranceFileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    
                    {/* NEW FILES TO BE ATTACHED */}
                    {insuranceDetailsData.attachedFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs font-medium text-gray-600 mb-2">New files:</p>
                        {insuranceDetailsData.attachedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                            <span>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* EXISTING DOCUMENTS - Edit Mode */}
                    {insuranceDetailsData.existingDocuments.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-gray-600 mb-2">Existing documents:</p>
                        <div className="space-y-2">
                          {insuranceDetailsData.existingDocuments.map((doc, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center gap-2 flex-1">
                                <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                  {doc.type === 'pdf' ? (
                                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                    </svg>
                                  ) : (
                                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{doc.file_name}</p>
                                  <p className="text-xs text-gray-500">{doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <a
                                  href={doc.file}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1 text-xs font-medium text-blue-600 bg-white border border-blue-600 rounded hover:bg-blue-600 hover:text-white transition-colors"
                                >
                                  View
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                                  title="Delete document"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* EXISTING DOCUMENTS - View Mode */}
                {isViewMode && insuranceDetailsData.existingDocuments.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attached Documents
                    </label>
                    <div className="space-y-2">
                      {insuranceDetailsData.existingDocuments.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-2 flex-1">
                            <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                              {doc.type === 'pdf' ? (
                                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{doc.file_name}</p>
                              <p className="text-xs text-gray-500">{doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}</p>
                            </div>
                          </div>
                          <a
                            href={doc.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 text-xs font-medium text-blue-600 bg-white border border-blue-600 rounded hover:bg-blue-600 hover:text-white transition-colors flex-shrink-0"
                          >
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show message if no documents in view mode */}
                {isViewMode && insuranceDetailsData.existingDocuments.length === 0 && (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <p className="text-sm text-gray-500">{t('noFilesAttached')}</p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleInsuranceDetailsModalClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  {isViewMode ? t('close') : t('cancel')}
                </button>
                {!isViewMode && (
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    {t('save')}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search Field */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('search')}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Insurance Table with Refresh Button */}
      <div className="overflow-x-auto bg-white rounded-lg shadow mb-8 w-full">
        <div className="flex justify-between items-center bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{t('vehicleInsurance')}</h3>
          <button 
            onClick={handleRefresh}
            className="bg-gray-600 hover:bg-gray-700 text-white px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base transition-colors flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {t('refresh')}
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading insurance data...</p>
          </div>
        ) : (
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                  {t('licensePlate')}
                </th>
                
                <th colSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                  {t('insuranceGE')}
                </th>
                
                <th colSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                  {t('insuranceRU')}
                </th>
                
                <th colSpan="2" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                  {t('insuranceAM')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  {t('actions')}
                </th>			  
              </tr>
              
              <tr>
                <th className="px-4 py-2 border-b border-r border-gray-200"></th>
                
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('start')}</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">{t('end')}</th>
                
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('start')}</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">{t('end')}</th>
                
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b">{t('start')}</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">{t('end')}</th>
                
                <th className="px-4 py-2 border-b border-gray-200"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVehicles.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center">
                      <Truck className="h-12 w-12 text-gray-400 mb-2" />
                      <p className="text-lg font-medium text-gray-900 mb-1">
                        {searchQuery ? 'No vehicles found' : 'No vehicles registered'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {searchQuery ? 'Try a different search term' : 'Get started by registering your first vehicle'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                pagedVehicles.map((vehicle) => (
                  <tr key={vehicle.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                      {vehicle.plate_number}
                    </td>
                    
						<td className={`px-4 py-3 whitespace-nowrap text-sm ${getInsuranceDateColor(getVehicleInsuranceByCountry(vehicle.id, 'ge')?.date_to)} border-r border-gray-200`}>
						  {getInsuranceDates(vehicle.id, 'ge').dateFrom}
						</td>
						<td className={`px-4 py-3 whitespace-nowrap text-sm ${getInsuranceDateColor(getVehicleInsuranceByCountry(vehicle.id, 'ge')?.date_to)} border-r border-gray-200`}>
						  {getInsuranceDates(vehicle.id, 'ge').dateTo}
						</td>
                    
					<td className={`px-4 py-3 whitespace-nowrap text-sm ${getInsuranceDateColor(getVehicleInsuranceByCountry(vehicle.id, 'ru')?.date_to)} border-r border-gray-200`}>
					  {getInsuranceDates(vehicle.id, 'ru').dateFrom}
					</td>
					<td className={`px-4 py-3 whitespace-nowrap text-sm ${getInsuranceDateColor(getVehicleInsuranceByCountry(vehicle.id, 'ru')?.date_to)} border-r border-gray-200`}>
					  {getInsuranceDates(vehicle.id, 'ru').dateTo}
					</td>

					
					<td className={`px-4 py-3 whitespace-nowrap text-sm ${getInsuranceDateColor(getVehicleInsuranceByCountry(vehicle.id, 'am')?.date_to)} border-r border-gray-200`}>
					  {getInsuranceDates(vehicle.id, 'am').dateFrom}
					</td>
					<td className={`px-4 py-3 whitespace-nowrap text-sm ${getInsuranceDateColor(getVehicleInsuranceByCountry(vehicle.id, 'am')?.date_to)} border-r border-gray-200`}>
					  {getInsuranceDates(vehicle.id, 'am').dateTo}
					</td>
                    
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex justify-center space-x-2">
                        <button 
                          onClick={() => handleEditInsurance(vehicle.id)}
                          className={`${
                            hasAnyInsurance(vehicle.id) ? 
                            'text-blue-600 hover:text-blue-900' : 
                            'text-green-600 hover:text-green-900'
                          }`}
                          title={hasAnyInsurance(vehicle.id) ? 
                            "Edit All Insurances" : 
                            "Add Insurances"
                          }
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        
                        <button 
                          onClick={() => handleViewInsurance(vehicle.id)}
                          className="text-gray-600 hover:text-gray-900"
                          title="View Insurance Details"
                        >
                          <Search className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
        {filteredVehicles.length > pageSize && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('show')}:</span>
              {[20, 50, 100].map(n => (
                <button key={n} onClick={() => { setPageSize(n); setCurrentPage(1); }}
                  className={`px-2 py-1 text-xs rounded ${pageSize === n ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {n}
                </button>
              ))}
              <span className="text-xs text-gray-500 ml-2">
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredVehicles.length)} / {filteredVehicles.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => p === '…'
                  ? <span key={`e${idx}`} className="px-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setCurrentPage(p)}
                      className={`px-2 py-1 text-xs rounded border ${currentPage === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 hover:bg-gray-50'}`}>
                      {p}
                    </button>
                )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">›</button>
            </div>
          </div>
        )}
      </div>

      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: '', type: '' })}
      />
    </div>
  );
};

export default InsurancePage;